import moment from "moment";
import ExerciseMetadataService from "services/ExerciseMetadataService";
import MetadataHelpers from "helpers/MetadataHelpers";
import MeasureAggregateService, { MeasureAggregate } from "services/MeasureAggregateService";
import { Exercise, MetadataType, Variants } from "types";
import type { ExercisePersonalBest } from "types/personal-best";
import { SortType } from "types/output-exercise";

export interface GetPersonalBestParams {
  exerciseId: string;
  metricId: string;
  sortType: SortType;
  type?: MetadataType;
  variant?: Variants | null;
}

/**
 * Service for fetching personal bests using the MeasureAggregate endpoints.
 */
class PersonalBestService {
  private static readonly START_DATE = moment("2019-01-01").startOf("day").toISOString();

  private static getEndDate(): string {
    return moment().endOf("day").toISOString();
  }

  /**
   * Fetches personal bests for multiple athletes.
   * Uses the min or max aggregate endpoint based on sortType.
   *
   * @param athleteIds - Array of athlete IDs to fetch PBs for
   * @param params.exerciseId - The exercise ID
   * @param params.metricId - The metric field (e.g., "meanPower", "peakForce")
   * @param params.sortType - 0 for ascending (lower is better), 1 for descending (higher is better)
   * @param params.variant - Optional variant for unilateral exercises ("Left" | "Right" | null)
   */
  static async getMany(athleteIds: string[], params: GetPersonalBestParams): Promise<MeasureAggregate[]> {
    const { exerciseId, metricId, sortType, type = MetadataType.EXERCISE, variant } = params;

    /**
     * The 1RM metrics are synthetic metrics attached to exercises by
     * `ExercisesWithOneRepMaxMetricProvider`, so they arrive here as `type: "Exercise"`.
     * The aggregate endpoints expect `type: "OneRepMax"` for them, so convert here to
     * stay consistent with `useFetchAggregatedMeasures`/`useFetchAggregatedSessions`.
     */
    const isOneRepMax = MetadataHelpers.usesOneRepMaxQueryType({ type, field: metricId });
    const resolvedType = isOneRepMax ? MetadataType.ONE_REP_MAX : type;

    // Both Exercise and OneRepMax requests are scoped to an exercise.
    const isExerciseScoped = resolvedType === MetadataType.EXERCISE || isOneRepMax;
    const service = sortType === 0 ? MeasureAggregateService.getAthletesMin : MeasureAggregateService.getAthletesMax;

    return service(athleteIds, {
      type: resolvedType,
      exerciseId: isExerciseScoped ? exerciseId : null,
      metricId,
      startDate: PersonalBestService.START_DATE,
      endDate: PersonalBestService.getEndDate(),
      variant: variant ?? null,
    });
  }

  /**
   * Fetches a single athlete's personal best for an exercise.
   */
  static async getOne(athleteId: string, params: GetPersonalBestParams): Promise<MeasureAggregate | null> {
    const results = await PersonalBestService.getMany([athleteId], params);
    return results.find((r) => r.athleteId === athleteId) ?? null;
  }

  /**
   * Fetches an athlete's personal best for the default metric of each exercise.
   * Duplicate exercises are fetched once and exercises without a default metric are left out.
   *
   * @important Temporary implementation, one min/max call per exercise. It will be replaced with a bulk personal
   * best endpoint once the backend team delivers it. The signature is final, so only the internals of this method
   * change and no caller is affected.
   */
  static async getForExercises(
    athleteId: string,
    exercises: Pick<Exercise, "id" | "metrics">[]
  ): Promise<ExercisePersonalBest[]> {
    const uniqueExercises = Array.from(new Map(exercises.map((exercise) => [exercise.id, exercise])).values());

    const results = await Promise.all(
      uniqueExercises.map(async (exercise): Promise<ExercisePersonalBest | null> => {
        const { field, sortType } = ExerciseMetadataService.defaultMetric(exercise);
        if (!field) return null;

        const personalBest = await PersonalBestService.getOne(athleteId, {
          exerciseId: exercise.id,
          metricId: field,
          sortType,
        });

        return {
          exerciseId: exercise.id,
          metricField: field,
          value: personalBest?.value ?? null,
          date: personalBest?.date ?? null,
          measurementId: personalBest?.id ?? null,
        };
      })
    );

    return results.filter((result): result is ExercisePersonalBest => result !== null);
  }
}

export default PersonalBestService;
